import { ImgWithFallback } from "@/components/img-with-fallback";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, Plus, Search, ArrowRight } from "lucide-react";
import { Link } from "wouter";
import { useState } from "react";
import type { Brand, Workspace } from "@shared/schema";
import { optimizedImg } from "@/lib/img";
import { getQueryFn } from "@/lib/queryClient";
import { ShareDialog } from "@/components/share-dialog";
import { useAuth } from "@/hooks/use-auth";

export default function BrandsPage() {
  const [search, setSearch] = useState("");
  const { user } = useAuth();
  const canCreateBrand = !!user?.isMaster || !!user?.canCreateBrands;

  const { data: workspaces } = useQuery<Workspace[]>({
    queryKey: ["/api/workspaces"],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });
  const workspaceId = workspaces?.[0]?.id;

  const { data: brands, isLoading } = useQuery<Brand[]>({
    queryKey: [`/api/brands?workspaceId=${workspaceId}`],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!workspaceId,
  });

  const filtered = brands?.filter(b =>
    b.name.toLowerCase().includes(search.toLowerCase())
  ) || [];

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap fade-in-up">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-brands-title">Brands</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage your brand portfolio</p>
        </div>
        {canCreateBrand && (
          <Link href="/brands/new">
            <Button className="rounded-full px-5" data-testid="button-create-brand">
              <Plus className="h-4 w-4 mr-1.5" /> New Brand
            </Button>
          </Link>
        )}
      </div>

      <div className="relative max-w-sm fade-in-up-delay-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search brands..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9 rounded-full"
          data-testid="input-search-brands"
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="border-border/50"><CardContent className="p-6"><Skeleton className="h-24 w-full" /></CardContent></Card>
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 fade-in-up-delay-2">
          {filtered.map(brand => (
            <Link key={brand.id} href={`/brands/${brand.id}`}>
              <Card className="cursor-pointer premium-card h-full group" data-testid={`card-brand-${brand.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors overflow-hidden">
                        <ImgWithFallback
                          src={brand.logoUrl ? optimizedImg(brand.logoUrl, 80, 70) : undefined}
                          alt={`${brand.name} logo`}
                          loading="lazy"
                          className="w-full h-full object-cover rounded-lg"
                          data-testid={`img-brand-logo-${brand.id}`}
                          fallback={<Building2 className="h-4 w-4 text-primary" />}
                        />
                      </div>
                      <div>
                        <CardTitle className="text-sm tracking-tight">{brand.name}</CardTitle>
                        {brand.category && (
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{brand.category}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1" onClick={e => e.preventDefault()}>
                      <ShareDialog
                        resourceType="brand"
                        resourceId={brand.id}
                        resourceName={brand.name}
                        workspaceId={workspaceId!}
                        iconOnly
                      />
                      <Badge variant={brand.status === "active" ? "default" : "secondary"} className="text-[10px] rounded-full px-2">
                        {brand.status}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{brand.summary || "No description"}</p>
                  <div className="flex items-center gap-1 mt-3 text-[10px] text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Open workspace <ArrowRight className="h-3 w-3" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card className="border-dashed border-border/50">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-semibold tracking-tight mb-1">No brands found</h3>
            <p className="text-sm text-muted-foreground" data-testid="text-brands-empty">
              {canCreateBrand
                ? "Create your first brand to start building strategy"
                : "You don't have access to any brands yet. Ask an admin to grant you access."}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
